import { gql, type TypedDocumentNode } from '@apollo/client'
import { useQuery } from '@apollo/client/react'
import {
  Badge,
  Button,
  Label,
  Popover,
  PopoverContent,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
  Separator,
  Spinner,
  Switch,
} from '@repo/shared-client/components/ui'
import { cn } from '@repo/shared-client/lib/utils'
import NotificationList from '@web/src/features/notifications/notification-list/notification-list'
import { useTranslation } from '@web/src/lib/intl/react'
import { limitBadgeNumber } from '@web/src/lib/utilsClient'
import { BellIcon, BellOffIcon, BellRingIcon, XIcon } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { usePushNotification } from 'src/hooks/usePushNotification'
import { usePwaInstall } from 'src/hooks/usePwaInstall'
import type { FindNotificationCountQuery, FindNotificationCountQueryVariables } from 'types/graphql'

const NOTIFICATION_INDICATOR_QUERY: TypedDocumentNode<FindNotificationCountQuery, FindNotificationCountQueryVariables> =
  gql`
    query FindNotificationCountQuery {
      notificationCount: notificationsCountCurrentUser
    }
  `

const NotificationIndicator = () => {
  const { t } = useTranslation()
  const queryResult = useQuery(NOTIFICATION_INDICATOR_QUERY, {
    fetchPolicy: 'no-cache',
    pollInterval: 1000 * 30,
    notifyOnNetworkStatusChange: true,
  })
  const { data, refetch } = queryResult

  const [popoverOpen, setPopoverOpen] = useState<boolean>(false)
  const { isInstalled } = usePwaInstall()
  const {
    isPushSupported,
    isPushReady,
    isPushEnabled,
    subscribeToPush,
    unsubscribeFromPush,
    isLoading,
    updateBadgeCount,
  } = usePushNotification()

  const handleRefreshNotificationsCount = () => {
    queryResult.refetch()
  }

  const handleTogglePushNotifications = async () => {
    if (isPushEnabled) {
      await unsubscribeFromPush()
    } else {
      await subscribeToPush()
    }
  }

  const handlePushMessage = useCallback(
    (event: MessageEvent) => {
      if (event.data && event.data.type === 'PUSH_RECEIVED') {
        refetch()
      }
    },
    [refetch]
  )

  const notificationCount = typeof data?.notificationCount === 'number' ? data.notificationCount : 0

  // Update badge count when notification count changes
  useEffect(() => {
    if (isPushEnabled && typeof data?.notificationCount === 'number') {
      updateBadgeCount(data.notificationCount)
    }
  }, [data?.notificationCount, isPushEnabled, updateBadgeCount])

  // The container receives worker messages regardless of which worker is active, so no need to wait for `ready`
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return

    navigator.serviceWorker.addEventListener('message', handlePushMessage)

    return () => {
      navigator.serviceWorker.removeEventListener('message', handlePushMessage)
    }
  }, [handlePushMessage])

  return (
    <Popover onOpenChange={setPopoverOpen} open={popoverOpen}>
      <PopoverTrigger
        render={
          <Button
            aria-label={t('Notifications')}
            className="relative overflow-visible"
            size="icon-lg"
            type="button"
            variant="ghost"
          />
        }
      >
        {isPushEnabled ? (
          <BellRingIcon aria-hidden className="size-7" />
        ) : isInstalled ? (
          <BellOffIcon aria-hidden className="size-7" />
        ) : (
          <BellIcon aria-hidden className="size-7" />
        )}
        {notificationCount > 0 ? (
          <Badge
            className="pointer-events-none absolute top-0 right-0 min-w-5 px-1 text-xs"
            size="sm"
            variant="default"
          >
            {limitBadgeNumber(notificationCount)}
          </Badge>
        ) : null}
      </PopoverTrigger>
      <PopoverContent className="w-[95vw] gap-0 p-0 sm:w-[480px]">
        <div className="flex items-start justify-between gap-2 px-2.5 pt-2.5">
          <PopoverHeader className="flex-1 gap-0 p-0">
            <PopoverTitle className="font-bold">{t('Notifications')}</PopoverTitle>
          </PopoverHeader>
          <Button
            aria-label={t('Close')}
            className="-mt-0.5 -mr-1 shrink-0"
            onClick={() => setPopoverOpen(false)}
            size="icon-sm"
            type="button"
            variant="ghost"
          >
            <XIcon className="size-4" />
          </Button>
        </div>
        <div className="flex flex-col gap-2 p-1">
          <NotificationList
            closePopup={() => setPopoverOpen(false)}
            onRefreshNotificationsCount={handleRefreshNotificationsCount}
          />

          {isPushSupported && isInstalled ? (
            <>
              <Separator />
              <div className="flex items-center justify-center gap-3 py-2 pr-4">
                <Label
                  className={cn('text-sm', isPushEnabled ? 'text-foreground' : 'text-muted-foreground')}
                  htmlFor="notification-push-switch"
                >
                  {t('Push Notifications')}
                </Label>
                {isLoading || !isPushReady ? (
                  <Spinner className="size-4" />
                ) : (
                  <Switch
                    checked={isPushEnabled}
                    id="notification-push-switch"
                    onClick={() => {
                      // onCheckedChange runs from a synthetic click, which iPadOS does not treat as a user gesture
                      handleTogglePushNotifications().catch(console.error)
                    }}
                  />
                )}
                <span className={cn('text-sm', isPushEnabled ? 'text-foreground' : 'text-muted-foreground')}>
                  {isPushEnabled ? t('On') : t('Off')}
                </span>
              </div>
            </>
          ) : null}
        </div>
      </PopoverContent>
    </Popover>
  )
}

export default NotificationIndicator
